import React, { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import {
  Alert,
  Button,
  Empty,
  Progress,
  Select,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppBarCMS } from '../../../../basic';
import { CollapseBlock, MainTitle, Spinner } from '../../../../common';
import PhdHelpHint from '../../../../common/Phd/PhdHelpHint.jsx';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import {
  useGetPhdDashboardQuery,
  useGetPhdSchoolYearsQuery,
  useGetPhdStudyFieldsQuery,
  useGetPhdSupervisorsQuery,
} from '../../../../../redux/services/phd.jsx';
import { routes } from '../../../../../utils/helpers/ApiClient.jsx';
import { resolvePhdDashboardAction } from './phdDashboardActions.js';
import './PhdDashboard.scss';

const { Text } = Typography;

const ITEM_LABELS = {
  profile_data: 'Profilové údaje',
  thesis: 'Dizertačná téma',
  individual_plan: 'Individuálny študijný plán',
  grades: 'Známky',
  annual_evaluation: 'Ročné hodnotenie',
  dissertation_exam_application: 'Prihláška na dizertačnú skúšku',
  subjects_exam_application: 'Prihláška na skúšku z predmetov',
  defense_application: 'Prihláška na obhajobu',
  exam_examiner_proposal: 'Návrh komisie pre dizertačnú skúšku',
  defense_examiner_proposal: 'Návrh komisie pre obhajobu',
  defense_files: 'Súbory obhájenej práce',
  dissertation_exam_result: 'Výsledok dizertačnej skúšky',
};

const STATUS_META = {
  missing: {
    label: 'Chýba',
    color: 'warning',
    help: 'Povinný záznam ešte neexistuje. Otvorte ponúknutý krok a vytvorte ho.',
  },
  draft: {
    label: 'Rozpracované',
    color: 'processing',
    help: 'Záznam existuje, ale ešte nie je dokončený alebo odovzdaný.',
  },
  submitted: {
    label: 'Odovzdané',
    color: 'cyan',
    help: 'Záznam bol odovzdaný a je súčasťou evidovaného priebehu štúdia.',
  },
  complete: {
    label: 'Dokončené',
    color: 'success',
    help: 'Krok je pre zvolený školský rok splnený.',
  },
  overdue: {
    label: 'Po termíne',
    color: 'error',
    help: 'Krok nie je splnený a jeho platný termín už uplynul. Otvorte opravu bez odkladu.',
  },
  blocked: {
    label: 'Blokované',
    color: 'default',
    help: 'Krok zatiaľ nemožno vykonať. Najprv opravte uvedený predchádzajúci krok.',
  },
  not_applicable: {
    label: 'Nevzťahuje sa',
    color: 'default',
    help: 'Krok sa na doktoranda v tomto školskom roku nevzťahuje a neznižuje dokončenie.',
  },
};

const STUDENT_STATE_META = [
  {
    key: 'active',
    field: 'active_students',
    label: 'Aktívni',
    help: 'Doktorand aktuálne študuje a jeho štúdium nebolo ukončené ani prerušené.',
    icon: <TeamOutlined />,
  },
  {
    key: 'finished',
    field: 'finished_students',
    label: 'Ukončení',
    help: 'Doktorand riadne ukončil štúdium (napr. obhájením dizertačnej práce).',
    icon: <TeamOutlined />,
  },
  {
    key: 'interrupted',
    field: 'interrupted_students',
    label: 'Prerušení',
    help: 'Štúdium doktoranda je aktuálne prerušené.',
    icon: <TeamOutlined />,
  },
  {
    key: 'expelled',
    field: 'expelled_students',
    label: 'Vylúčení',
    help: 'Doktorand bol zo štúdia vylúčený alebo štúdium inak neukončil úspešne.',
    icon: <TeamOutlined />,
  },
];

const ITEM_STATUS_SUMMARY = [
  {
    status: 'missing',
    field: 'missing_count',
    icon: <ExclamationCircleOutlined />,
    className: 'phd-dashboard__summary-item--warning',
  },
  { status: 'submitted', field: 'submitted_count', icon: <TeamOutlined /> },
  {
    status: 'complete',
    field: 'complete_count',
    icon: <CheckCircleOutlined />,
    className: 'phd-dashboard__summary-item--complete',
  },
  {
    status: 'overdue',
    field: 'overdue_count',
    icon: <ClockCircleOutlined />,
    className: 'phd-dashboard__summary-item--urgent',
  },
  {
    status: 'blocked',
    field: 'blocked_count',
    icon: <ExclamationCircleOutlined />,
  },
];

const STATE_VALUES = ['Active', 'Finished', 'Interrupted', 'Expelled'];
const DEFAULT_STUDENT_STATE = 'Active';
const SORT_VALUES = [
  ['overdue_count', 'Počet položiek po termíne'],
  ['missing_count', 'Počet chýbajúcich položiek'],
  ['completion_ratio', 'Miera dokončenia'],
  ['student_name', 'Meno doktoranda'],
];

const numericSearchParam = (searchParams, key, fallback = undefined) => {
  const value = Number(searchParams.get(key));
  return Number.isInteger(value) && value > 0 ? value : fallback;
};

const entityLabel = (entity) =>
  entity?.full_name ||
  entity?.display_name ||
  entity?.name ||
  [
    entity?.title_before,
    entity?.first_name,
    entity?.last_name,
    entity?.title_after,
  ]
    .filter(Boolean)
    .join(' ')
    .trim() ||
  String(entity?.id ?? '');

const PhdDashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageTitle = t('Prehľad doktorandského štúdia');
  const { data: schoolYears = [], isLoading: isLoadingSchoolYears } =
    useGetPhdSchoolYearsQuery();
  const { data: studyFields = [] } = useGetPhdStudyFieldsQuery();
  const { data: supervisors = [] } = useGetPhdSupervisorsQuery();

  const sortedSchoolYears = useMemo(
    () =>
      [...schoolYears].sort((left, right) => {
        const startComparison =
          dayjs(right.start_year).valueOf() - dayjs(left.start_year).valueOf();

        return startComparison || Number(right.id) - Number(left.id);
      }),
    [schoolYears],
  );
  const schoolYearOptions = useMemo(
    () =>
      sortedSchoolYears
        .filter(
          (schoolYear) => !dayjs(schoolYear.start_year).isAfter(dayjs(), 'day'),
        )
        .map((schoolYear) => ({
          value: schoolYear.id,
          label: `${dayjs(schoolYear.start_year).format('YYYY')} / ${dayjs(
            schoolYear.end_year,
          ).format('YYYY')}`,
        })),
    [sortedSchoolYears],
  );
  const defaultSchoolYearId = useMemo(() => {
    const today = dayjs();
    const currentSchoolYear = sortedSchoolYears.find(
      (schoolYear) =>
        !today.isBefore(dayjs(schoolYear.start_year), 'day') &&
        !today.isAfter(dayjs(schoolYear.end_year), 'day'),
    );

    return currentSchoolYear?.id ?? sortedSchoolYears[0]?.id;
  }, [sortedSchoolYears]);

  const selectedSchoolYearId = numericSearchParam(
    searchParams,
    'phd_school_year_id',
  );
  const isSelectedSchoolYearAvailable = schoolYearOptions.some(
    (option) => option.value === selectedSchoolYearId,
  );
  const schoolYearId =
    isSelectedSchoolYearAvailable || !selectedSchoolYearId
      ? selectedSchoolYearId
      : defaultSchoolYearId;
  const selectedSchoolYearLabel = useMemo(
    () =>
      schoolYearOptions.find((option) => option.value === schoolYearId)?.label,
    [schoolYearOptions, schoolYearId],
  );
  const selectedSchoolYear = sortedSchoolYears.find(
    (schoolYear) => Number(schoolYear.id) === Number(schoolYearId),
  );
  const isSelectedSchoolYearWritable = Boolean(
    selectedSchoolYear?.is_current === true &&
      selectedSchoolYear?.status === 'open',
  );
  const page = numericSearchParam(searchParams, 'page', 1);
  const perPage = numericSearchParam(searchParams, 'per_page', 25);

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  useEffect(() => {
    if (
      defaultSchoolYearId &&
      (!selectedSchoolYearId || !isSelectedSchoolYearAvailable)
    ) {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set('phd_school_year_id', String(defaultSchoolYearId));
          return next;
        },
        { replace: true },
      );
    }
  }, [
    defaultSchoolYearId,
    isSelectedSchoolYearAvailable,
    selectedSchoolYearId,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!searchParams.get('state')) {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set('state', DEFAULT_STUDENT_STATE);
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);

  const queryParams = useMemo(() => {
    if (!schoolYearId) {
      return null;
    }

    const params = {
      phd_school_year_id: schoolYearId,
      page,
      per_page: perPage,
      state: searchParams.get('state') || DEFAULT_STUDENT_STATE,
    };
    [
      'phd_study_field_id',
      'supervisor_id',
      'item_key',
      'item_status',
      'sort_by',
      'sort_direction',
    ].forEach((key) => {
      const value = searchParams.get(key);
      if (value) {
        params[key] = value;
      }
    });

    return params;
  }, [page, perPage, schoolYearId, searchParams]);

  const { data, isLoading, isFetching, error } = useGetPhdDashboardQuery(
    queryParams,
    { skip: !queryParams },
  );
  const summary = data?.summary || {};
  const pagination = data?.pagination || {};
  const expandedStudentId = numericSearchParam(
    searchParams,
    'expanded_student_id',
  );
  const expandedRowKeys = useMemo(() => {
    if (
      !expandedStudentId ||
      !(data?.items || []).some(
        (item) => Number(item.phd_student_id) === expandedStudentId,
      )
    ) {
      return [];
    }

    return [expandedStudentId];
  }, [data?.items, expandedStudentId]);

  useEffect(() => {
    if (!data || !expandedStudentId || expandedRowKeys.length) {
      return;
    }

    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('expanded_student_id');
        return next;
      },
      { replace: true },
    );
  }, [data, expandedRowKeys.length, expandedStudentId, setSearchParams]);

  const updateFilters = (changes) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      Object.entries(changes).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      });
      if (!Object.prototype.hasOwnProperty.call(changes, 'page')) {
        next.set('page', '1');
      }
      return next;
    });
  };

  const handleExpandedRowsChange = (keys) => {
    const expandedId = keys.at(-1);

    setSearchParams((current) => {
      const next = new URLSearchParams(current);

      if (expandedId === undefined) {
        next.delete('expanded_student_id');
      } else {
        next.set('expanded_student_id', String(expandedId));
      }

      return next;
    });
  };

  const renderStatusTag = (status) => {
    const meta = STATUS_META[status] || STATUS_META.not_applicable;
    return (
      <span>
        <Tag color={meta.color}>{t(meta.label)}</Tag>
        <PhdHelpHint
          label={t('Vysvetlenie stavu {{status}}', {
            status: t(meta.label),
          })}
          title={t(meta.help)}
        />
      </span>
    );
  };

  const expandedRowRender = (record) => (
    <div className="phd-dashboard__checklist">
      {(record.items || [])
        .filter((item) => item.status !== 'not_applicable')
        .map((item) => {
          const actionUrl = resolvePhdDashboardAction(item);
          return (
            <div
              key={item.item_key}
              className={`phd-dashboard__item phd-dashboard__item--${item.status}`}
            >
              <div className="phd-dashboard__item-heading">
                <Text strong>
                  {t(ITEM_LABELS[item.item_key] || item.item_key)}
                </Text>
                {renderStatusTag(item.status)}
              </div>
              <Text type="secondary">
                {t(item.reason_key, item.reason_params || {})}
              </Text>
              {(item.blocked_by || []).length ? (
                <Alert
                  type="warning"
                  showIcon
                  message={t('Najprv dokončite: {{steps}}', {
                    steps: item.blocked_by
                      .map((key) => t(ITEM_LABELS[key] || key))
                      .join(', '),
                  })}
                  description={t(
                    'Blokovaný krok sa sprístupní po oprave uvedeného predchádzajúceho kroku.',
                  )}
                />
              ) : null}
              {(item.issues || []).map((issue) => (
                <Alert
                  key={issue.key}
                  message={t(issue.key, issue.params || {})}
                  description={t(
                    'Problém s kvalitou dát nemení stav kroku. Opravte zdrojový záznam cez ponúknutú akciu alebo kontaktujte administrátora doktorandského štúdia.',
                  )}
                  type={issue.severity === 'error' ? 'error' : 'warning'}
                  showIcon
                />
              ))}
              <div className="phd-dashboard__item-footer">
                <Text type="secondary">
                  {item.deadline
                    ? `${t('Platný termín')}: ${dayjs(item.deadline).format('DD.MM.YYYY')}`
                    : t('Bez termínu')}
                  {item.deadline ? (
                    <PhdHelpHint
                      label={t('Vysvetlenie platného termínu')}
                      title={t(
                        'Platný termín zohľadňuje pravidlo štúdia, schválenú individuálnu úpravu a prerušenia evidované v profile doktoranda.',
                      )}
                    />
                  ) : null}
                </Text>
                {item.action_key ? (
                  <span>
                    <Button
                      type="link"
                      size="small"
                      disabled={!actionUrl}
                      onClick={() => actionUrl && navigate(actionUrl)}
                      icon={<ArrowRightOutlined />}
                    >
                      {t('Otvoriť opravný krok')}
                    </Button>
                    {!actionUrl ? (
                      <Text type="secondary">
                        {t(
                          'Opravný krok nemožno otvoriť z prehľadu. Otvorte príslušnú časť správy doktorandského štúdia.',
                        )}
                      </Text>
                    ) : null}
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
    </div>
  );

  const columns = [
    {
      title: t('Doktorand'),
      dataIndex: 'student_name',
      key: 'student_name',
      render: (value) => <Text strong>{value || '—'}</Text>,
    },
    {
      title: t('Školiteľ'),
      dataIndex: 'supervisor_name',
      key: 'supervisor_name',
      render: (value) => value || '—',
    },
    {
      title: t('Stav'),
      dataIndex: 'state',
      key: 'state',
      width: 130,
      render: (value) => <Tag>{t(value)}</Tag>,
    },
    {
      title: t('Chýba'),
      dataIndex: 'missing_count',
      key: 'missing_count',
      width: 95,
      align: 'center',
      render: (value) => <span className="phd-dashboard__count">{value}</span>,
    },
    {
      title: t('Po termíne'),
      dataIndex: 'overdue_count',
      key: 'overdue_count',
      width: 110,
      align: 'center',
      render: (value) => (
        <span className={value ? 'phd-dashboard__count--danger' : ''}>
          {value}
        </span>
      ),
    },
    {
      title: t('Dokončenie'),
      dataIndex: 'completion_ratio',
      key: 'completion_ratio',
      width: 190,
      render: (value) => (
        <Progress
          percent={value}
          size="small"
          status={value === 100 ? 'success' : 'normal'}
        />
      ),
    },
  ];

  if (isLoadingSchoolYears || (schoolYearId && isLoading)) {
    return <Spinner />;
  }

  return (
    <>
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsDashboard}
        backUrlTooltip={t('Nástenka')}
        permissionsBasic="phd-dashboard"
      />
      <div className="phd-dashboard" style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako pracovať s prehľadom doktorandského štúdia?')}
          description={t(
            'Vyberte školský rok, rozbaľte doktoranda a riešte najprv chýbajúce alebo blokujúce kroky. Súhrn vždy počíta celý filtrovaný súbor; položka Nevzťahuje sa dokončenie neznižuje.',
          )}
        />
        {!isSelectedSchoolYearWritable && schoolYearId ? (
          <Alert
            type="info"
            showIcon
            title={t('Historický školský rok je iba na čítanie')}
            description={t(
              'Chýbajúce záznamy nemožno vytvoriť spätne cez bežný formulár. Použite povolený korekčný postup alebo kontaktujte administrátora doktorandského štúdia.',
            )}
          />
        ) : null}
        <CollapseBlock>
          <MainTitle title={pageTitle} />
          <Text className="phd-dashboard__description" type="secondary">
            {t('Prehľad evidencie, povinností a termínov doktorandov.')}
          </Text>
          {selectedSchoolYearLabel ? (
            <Text className="phd-dashboard__summary-caption" type="secondary">
              {t('Súhrn za školský rok {{schoolYear}}', {
                schoolYear: selectedSchoolYearLabel,
              })}
            </Text>
          ) : null}
          <div className="phd-dashboard__summary-strip phd-dashboard__summary-strip--states">
            {STUDENT_STATE_META.map(({ key, field, label, help, icon }) => (
              <div key={key} className="phd-dashboard__summary-item">
                <Statistic
                  title={
                    <span>
                      {t(label)}
                      <PhdHelpHint
                        label={t('Vysvetlenie stavu {{state}}', {
                          state: t(label),
                        })}
                        title={t(help)}
                      />
                    </span>
                  }
                  value={summary[field] ?? 0}
                  prefix={icon}
                />
              </div>
            ))}
          </div>
          <div className="phd-dashboard__summary-strip phd-dashboard__summary-strip--types">
            {ITEM_STATUS_SUMMARY.map(({ status, field, icon, className }) => (
              <div
                key={status}
                className={`phd-dashboard__summary-item${className ? ` ${className}` : ''}`}
              >
                <Statistic
                  title={
                    <span>
                      {t(STATUS_META[status].label)}
                      <PhdHelpHint
                        label={t('Vysvetlenie stavu {{status}}', {
                          status: t(STATUS_META[status].label),
                        })}
                        title={t(STATUS_META[status].help)}
                      />
                    </span>
                  }
                  value={summary[field] ?? 0}
                  prefix={icon}
                />
              </div>
            ))}
          </div>
        </CollapseBlock>

        <CollapseBlock collapsible title={t('Filtre a radenie')}>
          <div className="phd-dashboard__filters">
            <div className="phd-dashboard__filter-row">
              <div className="phd-dashboard__filter-row-label">
                <Text strong>{t('Výber')}</Text>
              </div>
              <Select
                aria-label={t('Školský rok')}
                value={schoolYearId}
                options={schoolYearOptions}
                onChange={(value) =>
                  updateFilters({ phd_school_year_id: value })
                }
                placeholder={t('Školský rok')}
                className="phd-dashboard__filter phd-dashboard__filter--year"
              />
              <Select
                allowClear
                aria-label={t('Študijný odbor')}
                value={numericSearchParam(searchParams, 'phd_study_field_id')}
                options={studyFields.map((field) => ({
                  value: field.id,
                  label: field.code
                    ? `${field.code} — ${field.study_field || field.name}`
                    : field.study_field || field.name,
                }))}
                onChange={(value) =>
                  updateFilters({ phd_study_field_id: value })
                }
                placeholder={t('Študijný odbor')}
                className="phd-dashboard__filter phd-dashboard__filter--field"
                showSearch
                optionFilterProp="label"
              />
              <Select
                allowClear
                aria-label={t('Školiteľ')}
                value={numericSearchParam(searchParams, 'supervisor_id')}
                options={supervisors.map((supervisor) => ({
                  value: supervisor.person_id ?? supervisor.id,
                  label: entityLabel(supervisor.person || supervisor),
                }))}
                onChange={(value) => updateFilters({ supervisor_id: value })}
                placeholder={t('Školiteľ')}
                className="phd-dashboard__filter phd-dashboard__filter--supervisor"
                showSearch
                optionFilterProp="label"
              />
              <Select
                allowClear
                aria-label={t('Stav doktoranda')}
                value={searchParams.get('state') || DEFAULT_STUDENT_STATE}
                options={STATE_VALUES.map((state) => ({
                  value: state,
                  label: t(state),
                }))}
                onChange={(value) => updateFilters({ state: value })}
                placeholder={t('Stav doktoranda')}
                className="phd-dashboard__filter phd-dashboard__filter--state"
              />
            </div>
            <div className="phd-dashboard__filter-row">
              <div className="phd-dashboard__filter-row-label">
                <Text strong>{t('Výsledky')}</Text>
              </div>
              <Select
                allowClear
                aria-label={t('Krok')}
                value={searchParams.get('item_key') || undefined}
                options={Object.entries(ITEM_LABELS).map(([value, label]) => ({
                  value,
                  label: t(label),
                }))}
                onChange={(value) =>
                  updateFilters({
                    item_key: value,
                    item_status: value
                      ? searchParams.get('item_status')
                      : undefined,
                  })
                }
                placeholder={t('Krok')}
                className="phd-dashboard__filter phd-dashboard__filter--status"
                showSearch
                optionFilterProp="label"
              />
              <Select
                allowClear
                aria-label={t('Stav kroku')}
                value={searchParams.get('item_status') || undefined}
                options={Object.entries(STATUS_META).map(([value, meta]) => ({
                  value,
                  label: t(meta.label),
                }))}
                onChange={(value) => updateFilters({ item_status: value })}
                placeholder={t('Stav kroku')}
                className="phd-dashboard__filter phd-dashboard__filter--status"
              />
              <Select
                allowClear
                aria-label={t('Zoradiť podľa')}
                value={searchParams.get('sort_by') || undefined}
                options={SORT_VALUES.map(([value, label]) => ({
                  value,
                  label: t(label),
                }))}
                onChange={(value) => updateFilters({ sort_by: value })}
                placeholder={t('Zoradiť podľa')}
                className="phd-dashboard__filter phd-dashboard__filter--sort"
              />
              <Select
                aria-label={t('Smer radenia')}
                value={searchParams.get('sort_direction') || 'asc'}
                options={[
                  { value: 'asc', label: t('Vzostupne') },
                  { value: 'desc', label: t('Zostupne') },
                ]}
                onChange={(value) => updateFilters({ sort_direction: value })}
                className="phd-dashboard__filter phd-dashboard__filter--direction"
              />
              <div className="phd-dashboard__filter-actions">
                <Button
                  onClick={() =>
                    setSearchParams({
                      phd_school_year_id: schoolYearId,
                      state: DEFAULT_STUDENT_STATE,
                    })
                  }
                >
                  {t('Vyčistiť filtre')}
                </Button>
              </div>
            </div>
          </div>
        </CollapseBlock>

        {error ? (
          <Alert
            type="error"
            showIcon
            message={t('Prehľad sa nepodarilo načítať.')}
            description={t(
              'Skontrolujte pripojenie a filtre, potom stránku obnovte. Ak chyba trvá, kontaktujte administrátora doktorandského štúdia.',
            )}
          />
        ) : null}

        <CollapseBlock title={t('Prehľad doktorandov')}>
          <div className="phd-dashboard__table-shell">
            <Table
              rowKey="phd_student_id"
              columns={columns}
              dataSource={data?.items || []}
              loading={isFetching && !isLoading}
              expandable={{
                expandedRowRender,
                expandedRowKeys,
                onExpandedRowsChange: handleExpandedRowsChange,
              }}
              locale={{
                emptyText: (
                  <Empty
                    description={t(
                      'Žiadni doktorandi nezodpovedajú zvolenému roku a filtrom. Upravte alebo vyčistite filtre.',
                    )}
                  />
                ),
              }}
              scroll={{ x: 760 }}
              pagination={{
                current: pagination.current_page ?? page,
                pageSize: pagination.per_page ?? perPage,
                total: pagination.total ?? 0,
                showSizeChanger: true,
                pageSizeOptions: [10, 25, 50, 100],
                showTotal: (total) => `${t('Spolu')}: ${total}`,
              }}
              onChange={(nextPagination) =>
                updateFilters({
                  page: nextPagination.current,
                  per_page: nextPagination.pageSize,
                })
              }
            />
          </div>
        </CollapseBlock>
      </div>
    </>
  );
};

export default PhdDashboardPage;
